import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Trash2, Check } from 'lucide-react';
import { Customer } from '../../../shared/types';
import { CustomerForm, defaultLohnscheinLauf, type LohnscheinLaufFormConfig } from './CustomerForm';
import { CustomerDatevPanel } from './CustomerDatevPanel';
import { supabase } from '../../../shared/services/supabase';
import { IndividualDeadlinesManager } from './IndividualDeadlinesManager';
import { CustomAttachmentsManager } from './CustomAttachmentsManager';
import { CustomTodosManager } from './CustomTodosManager';
import { ContactsManager } from './ContactsManager';
import { VersandSichtbarkeitPanel } from './VersandSichtbarkeitPanel';
import { BenachrichtigungszentrumSchalter } from './BenachrichtigungszentrumSchalter';
import { useCustomerUpdate } from '../hooks/useCustomerUpdate';
import { validateCustomerData } from '../utils/customerValidation';
import { customerService } from '../../../services/customer/CustomerService';
import { invoiceService } from '../../../services/invoice/invoiceService';
import { zusatzleistungenService } from '../../../services/zusatzleistungen/zusatzleistungenService';
import { CustomerTicketsPanel } from '../../tickets/components/CustomerTicketsPanel';

export type EditCustomerModalTab = 'stammdaten' | 'ansprechpartner' | 'versand_sichtbarkeit' | 'lohnlauf' | 'steuer_sozialversicherung' | 'rechnungsdaten' | 'datev' | 'fristen' | 'anhaenge' | 'todos' | 'tickets';

interface EditCustomerModalProps {
  isOpen: boolean;
  customer: Customer;
  onClose: () => void;
  onSuccess: () => void;
  /** Reiter, mit dem das Fenster startet (Standard: Stammdaten). */
  initialTab?: EditCustomerModalTab;
}

export const EditCustomerModal: React.FC<EditCustomerModalProps> = ({
  isOpen,
  customer,
  onClose,
  onSuccess,
  initialTab
}) => {
  const [formData, setFormData] = useState<Partial<Customer>>({});
  const [activeTab, setActiveTab] = useState<EditCustomerModalTab>(initialTab ?? 'stammdaten');
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Zwei-Lauf-Modell: Lohnschein-Konfiguration pro Lauf (aus der Tabelle geladen).
  const [lohnscheinConfigs, setLohnscheinConfigs] = useState<LohnscheinLaufFormConfig[]>([defaultLohnscheinLauf(1), defaultLohnscheinLauf(2)]);
  const [deadlinesUpdated, setDeadlinesUpdated] = useState(false);
  const [attachmentsUpdated, setAttachmentsUpdated] = useState(false);
  const [todosUpdated, setTodosUpdated] = useState(false);
  const [contactsUpdated, setContactsUpdated] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pricingValue, setPricingValue] = useState<number | null>(null);
  const [stundensatzValue, setStundensatzValue] = useState<number | null>(null);
  // Kurzer "Gespeichert"-Hinweis nach erfolgreichem Speichern; das Modal bleibt geöffnet
  const [savedNotice, setSavedNotice] = useState(false);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ungespeicherte Aenderungen auf den Formular-Reitern
  const [isDirty, setIsDirty] = useState(false);
  // Es wurde etwas gespeichert -> Liste beim Schliessen einmalig aktualisieren
  const [needsRefresh, setNeedsRefresh] = useState(false);
  // Sichtbare Fehlermeldung, falls das Speichern fehlschlaegt
  const [saveError, setSaveError] = useState<string | null>(null);

  const { updateCustomer, isUpdating } = useCustomerUpdate();

  // Initialisiere Formular mit Kundendaten
  // HINWEIS: Kontaktfelder (email, email_cc, vorname, nachname, anrede, geschlecht, secure_download_passwort)
  // werden NICHT mehr hier verwaltet, sondern über den Ansprechpartner-Tab (ContactsManager)
  useEffect(() => {
    if (isOpen && customer) {
      setFormData({
        company: customer.company,
        telefon: customer.telefon,
        beraternummer: customer.beraternummer,
        mandantennummer: customer.mandantennummer,
        abrechnungsmethode: customer.abrechnungsmethode,
        erster_lohnlauf_tag: customer.erster_lohnlauf_tag,
        erster_lohnlauf_verschiebung: customer.erster_lohnlauf_verschiebung,
        erster_lohnlauf_monat_offset: customer.erster_lohnlauf_monat_offset,
        erster_lohnlauf_frist_ab_anlieferung: customer.erster_lohnlauf_frist_ab_anlieferung || false,
        zweiter_lohnlauf_tag: customer.zweiter_lohnlauf_tag,
        zweiter_lohnlauf_verschiebung: customer.zweiter_lohnlauf_verschiebung,
        zweiter_lohnlauf_monat_offset: customer.zweiter_lohnlauf_monat_offset,
        zweiter_lohnlauf_frist_ab_anlieferung: customer.zweiter_lohnlauf_frist_ab_anlieferung || false,
        // Lohnschein-Versand an Arbeitnehmer (Stufe 3.2)
        lohnschein_versand_tag: customer.lohnschein_versand_tag,
        lohnschein_versand_verschiebung: customer.lohnschein_versand_verschiebung,
        lohnschein_versand_monat_offset: customer.lohnschein_versand_monat_offset,
        lohnschein_ohne_passwort: customer.lohnschein_ohne_passwort ?? false,
        lohnschein_versand_bezug_lohnlauf: customer.lohnschein_versand_bezug_lohnlauf,
        lohnschein_versand_bezug_werktage_offset: customer.lohnschein_versand_bezug_werktage_offset,
        // OPPM Gehaltsdokumente mit QR-Code
        oppm_qr_versand_tag: customer.oppm_qr_versand_tag,
        oppm_qr_versand_verschiebung: customer.oppm_qr_versand_verschiebung,
        oppm_qr_versand_monat_offset: customer.oppm_qr_versand_monat_offset,
        oppm_qr_versand_bezug_lohnlauf: customer.oppm_qr_versand_bezug_lohnlauf,
        oppm_qr_versand_bezug_werktage_offset: customer.oppm_qr_versand_bezug_werktage_offset,
        // OPPM Monatsübersichten mit QR-Code
        monatsuebersicht_qr_versand_tag: customer.monatsuebersicht_qr_versand_tag,
        monatsuebersicht_qr_versand_verschiebung: customer.monatsuebersicht_qr_versand_verschiebung,
        monatsuebersicht_qr_versand_monat_offset: customer.monatsuebersicht_qr_versand_monat_offset,
        monatsuebersicht_qr_versand_bezug_lohnlauf: customer.monatsuebersicht_qr_versand_bezug_lohnlauf,
        monatsuebersicht_qr_versand_bezug_werktage_offset: customer.monatsuebersicht_qr_versand_bezug_werktage_offset,
        lohnsteuer_zeitraum: customer.lohnsteuer_zeitraum,
        sv_pflicht: customer.sv_pflicht,
        schaetzmandat: customer.schaetzmandat,
        versandmethode: customer.versandmethode,
        postalischer_versand: customer.postalischer_versand,
        im_auftrag_von: customer.im_auftrag_von,
        auftraggeber_id: customer.auftraggeber_id,
        customer_group_id: customer.customer_group_id,
        zustaendiger_mitarbeiter_id: customer.zustaendiger_mitarbeiter_id,
        duz_mitarbeiter_ids: customer.duz_mitarbeiter_ids,
        zendesk_tag: customer.zendesk_tag,
        // Rechnungsdaten
        strasse: customer.strasse,
        plz: customer.plz,
        ort: customer.ort,
        land: customer.land || 'Deutschland',
        kundennummer_rechnung: customer.kundennummer_rechnung,
        rechnungs_email: customer.rechnungs_email,
        // Abweichender Firmenname für die Rechnung. Fehlte hier zuvor, dadurch
        // zeigte das Feld im Rechnungsdaten-Reiter auch bei gepflegtem Wert leer an.
        company_rechnung: customer.company_rechnung,
        rechnung_anonyme_anrede: customer.rechnung_anonyme_anrede || false,
        // Abweichender Rechnungsempfänger (Person im Adressfeld der Rechnung)
        rechnung_empfaenger_anrede: customer.rechnung_empfaenger_anrede,
        rechnung_empfaenger_vorname: customer.rechnung_empfaenger_vorname,
        rechnung_empfaenger_nachname: customer.rechnung_empfaenger_nachname,
        // Vertragsdaten (nur Direktkunden)
        vertragsschluss: customer.vertragsschluss,
        zusammenarbeit_beginn: customer.zusammenarbeit_beginn,
        // Zahlungsart & Lastschrift
        zahlungsart: customer.zahlungsart || 'ueberweisung',
        lastschrift_iban: customer.lastschrift_iban,
        lastschrift_bic: customer.lastschrift_bic,
        lastschrift_kontoinhaber: customer.lastschrift_kontoinhaber,
        lastschrift_mandatsreferenz: customer.lastschrift_mandatsreferenz,
        lastschrift_mandatsdatum: customer.lastschrift_mandatsdatum,
        lastschrift_mandatsart: customer.lastschrift_mandatsart,
        // Reverse Charge
        reverse_charge: customer.reverse_charge || false,
        ust_id_kunde: customer.ust_id_kunde,
        // Steuer-ID-Anträge: Steuernummer (Lohnsteuer) und Betriebsstättenfinanzamt
        lohnsteuer_steuernummer: customer.lohnsteuer_steuernummer,
        finanzamt_id: customer.finanzamt_id
      });
      setErrors({});
      setActiveTab(initialTab ?? 'stammdaten');
      setDeadlinesUpdated(false);
      setAttachmentsUpdated(false);
      setTodosUpdated(false);
      setContactsUpdated(false);
      setPricingValue(null);
      setStundensatzValue(null);
      setSavedNotice(false);
      setIsDirty(false);
      setNeedsRefresh(false);
      setSaveError(null);
    }
  }, [isOpen, customer, initialTab]);

  // Lade Preis pro Arbeitnehmer aus customer_pricing Tabelle
  useEffect(() => {
    if (isOpen && customer?.id) {
      const loadPricing = async () => {
        const pricing = await invoiceService.getCurrentPricing(customer.id);
        setPricingValue(pricing ? Number(pricing.preis_pro_arbeitnehmer) : null);
      };
      loadPricing();
    }
  }, [isOpen, customer?.id]);

  // Lade Stundensatz aus customer_stundensatz Tabelle
  useEffect(() => {
    if (isOpen && customer?.id) {
      const loadStundensatz = async () => {
        const satz = await zusatzleistungenService.getCurrentStundensatz(customer.id);
        setStundensatzValue(satz ? Number(satz.stundensatz) : null);
      };
      loadStundensatz();
    }
  }, [isOpen, customer?.id]);

  // Laufenden Timer für die Gespeichert-Anzeige beim Schließen der Komponente stoppen
  useEffect(() => {
    return () => {
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
      }
    };
  }, []);

  // Zwei-Lauf-Modell: bestehende Lohnschein-Konfiguration des Kunden laden.
  useEffect(() => {
    if (isOpen && customer) {
      (async () => {
        const { data } = await supabase
          .from('customer_lohnschein_versand_configs')
          .select('lohnlauf, tag, verschiebung, monat_offset, bezug_werktage_offset, is_active')
          .eq('customer_id', customer.id);
        const geladen: LohnscheinLaufFormConfig[] = [defaultLohnscheinLauf(1), defaultLohnscheinLauf(2)].map(d => {
          const row = (data || []).find((r: any) => r.lohnlauf === d.lohnlauf);
          if (!row) return d;
          const relativ = row.bezug_werktage_offset !== null && row.bezug_werktage_offset !== undefined;
          return {
            lohnlauf: d.lohnlauf,
            aktiv: !!row.is_active,
            modus: relativ ? 'relativ' : 'fest',
            tag: row.tag ?? null,
            verschiebung: row.verschiebung ?? null,
            monat_offset: row.monat_offset ?? 0,
            bezug_werktage_offset: row.bezug_werktage_offset ?? null
          };
        });
        setLohnscheinConfigs(geladen);
      })();
    }
  }, [isOpen, customer]);

  // Zentrales Schliessen: warnt bei ungespeicherten Aenderungen und aktualisiert
  // die Liste nur einmalig, falls zwischenzeitlich etwas gespeichert wurde.
  const handleClose = useCallback(() => {
    if (isDirty) {
      const ok = window.confirm('Es gibt ungespeicherte Änderungen. Möchten Sie das Fenster schließen und die Änderungen verwerfen?');
      if (!ok) return;
    }
    if (needsRefresh) {
      onSuccess();
    }
    onClose();
  }, [isDirty, needsRefresh, onSuccess, onClose]);

  // Schliessen per Escape-Taste (mit demselben Schutz vor Datenverlust)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const handleFieldChange = (field: keyof Customer, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setIsDirty(true);
    if (saveError) setSaveError(null);
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Zeigt nach erfolgreichem Speichern kurz "Gespeichert" an. Das Modal bleibt
  // bewusst geöffnet; ein bereits laufender Timer wird vorher zurückgesetzt.
  const triggerSavedNotice = () => {
    setSavedNotice(true);
    if (savedTimeoutRef.current) {
      clearTimeout(savedTimeoutRef.current);
    }
    savedTimeoutRef.current = setTimeout(() => {
      setSavedNotice(false);
    }, 2500);
  };

  const handleLohnscheinConfigChange = (lohnlauf: 1 | 2, patch: Partial<LohnscheinLaufFormConfig>) => {
    setLohnscheinConfigs(prev => prev.map(c => c.lohnlauf === lohnlauf ? { ...c, ...patch } : c));
    setIsDirty(true);
  };

  // Explizites Verwerfen: schliesst ohne Rueckfrage, weil die Absicht eindeutig
  // ist. Die Liste wird aktualisiert, falls zwischendurch etwas gespeichert wurde.
  const handleDiscard = () => {
    if (needsRefresh) {
      onSuccess();
    }
    onClose();
  };

  const handleSave = async () => {
    // Bei Fristen, Anhänge, To-Dos, Ansprechpartner, Versand, DATEV und Tickets Tab nur schließen
    if (activeTab === 'fristen' || activeTab === 'anhaenge' || activeTab === 'todos' || activeTab === 'ansprechpartner' || activeTab === 'versand_sichtbarkeit' || activeTab === 'datev' || activeTab === 'tickets') {
      if (deadlinesUpdated || attachmentsUpdated || todosUpdated || contactsUpdated) {
        onSuccess();
      }
      onClose();
      return;
    }

    setSaveError(null);
    // Validierung für andere Tabs - Kontaktfelder überspringen (werden im Ansprechpartner-Tab verwaltet)
    const validationResult = validateCustomerData(formData, { skipContactFields: true });
    if (!validationResult.valid) {
      setErrors(validationResult.errors);
      // Wechsle zum Tab mit dem ersten Fehler
      const firstErrorField = Object.keys(validationResult.errors)[0];
      if (['company', 'beraternummer', 'mandantennummer', 'abrechnungsmethode'].includes(firstErrorField)) {
        setActiveTab('stammdaten');
      } else if (['erster_lohnlauf_tag', 'zweiter_lohnlauf_tag', 'lohnschein_versand_tag'].includes(firstErrorField)) {
        setActiveTab('lohnlauf');
      } else if (['rechnung_empfaenger_anrede', 'rechnung_empfaenger_vorname', 'rechnung_empfaenger_nachname'].includes(firstErrorField)) {
        setActiveTab('rechnungsdaten');
      } else {
        setActiveTab('steuer_sozialversicherung');
      }
      return;
    }

    try {
      await updateCustomer(customer.id, formData);

      // Zwei-Lauf-Modell: Lohnschein-Konfiguration pro Lauf schreiben. Aktive
      // Laeufe per Upsert, abgeschaltete Laeufe loeschen.
      for (const cfg of lohnscheinConfigs) {
        if (cfg.aktiv) {
          await supabase.from('customer_lohnschein_versand_configs').upsert({
            customer_id: customer.id,
            lohnlauf: cfg.lohnlauf,
            tag: cfg.modus === 'fest' ? cfg.tag : null,
            verschiebung: cfg.modus === 'fest' ? cfg.verschiebung : null,
            monat_offset: cfg.modus === 'fest' ? (cfg.monat_offset ?? 0) : null,
            bezug_werktage_offset: cfg.modus === 'relativ' ? (cfg.bezug_werktage_offset ?? 1) : null,
            is_active: true
          }, { onConflict: 'customer_id,lohnlauf' });
        } else {
          await supabase.from('customer_lohnschein_versand_configs')
            .delete().eq('customer_id', customer.id).eq('lohnlauf', cfg.lohnlauf);
        }
      }
      
      // Preis pro Arbeitnehmer separat speichern (separate Tabelle customer_pricing)
      if (activeTab === 'rechnungsdaten' && pricingValue !== null) {
        await invoiceService.savePricing(customer.id, pricingValue);
      }

      // Stundensatz separat speichern (separate Tabelle customer_stundensatz)
      if (activeTab === 'rechnungsdaten' && stundensatzValue !== null) {
        await zusatzleistungenService.saveStundensatz(customer.id, stundensatzValue);
      }
      
      // Kein Live-Neuladen der Liste mehr (das verursachte das sichtbare
      // Aufblitzen im Hintergrund). Stattdessen wird beim Schliessen einmalig
      // aktualisiert. Das Modal bleibt bewusst geöffnet.
      setIsDirty(false);
      setNeedsRefresh(true);
      triggerSavedNotice();
    } catch (error) {
      console.error('Error updating customer:', error);
      setSaveError('Speichern fehlgeschlagen. Bitte prüfen Sie Ihre Eingaben und versuchen Sie es erneut.');
    }
  };

  const handleDeadlinesUpdate = () => {
    setDeadlinesUpdated(true);
    setNeedsRefresh(true);
  };

  const handleAttachmentsUpdate = () => {
    setAttachmentsUpdated(true);
    setNeedsRefresh(true);
  };

  const handleTodosUpdate = () => {
    setTodosUpdated(true);
    setNeedsRefresh(true);
  };

  const handleContactsUpdate = () => {
    setContactsUpdated(true);
    setNeedsRefresh(true);
  };

  const handleDeleteCustomer = async () => {
    setIsDeleting(true);
    try {
      const result = await customerService.softDeleteCustomer(customer.id);
      if (result.success) {
        onSuccess(); // Refresh the list
        onClose();
      } else {
        alert(`Fehler beim Löschen: ${result.error || 'Unbekannter Fehler'}`);
        setIsDeleting(false);
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Unerwarteter Fehler beim Löschen des Kunden');
      setIsDeleting(false);
    }
  };

  // Tabs Liste - Ansprechpartner an Position 2
  const tabs = [
    { id: 'stammdaten', label: 'Stammdaten' },
    { id: 'ansprechpartner', label: 'Ansprechpartner' },
    { id: 'versand_sichtbarkeit', label: 'Versand & Sichtbarkeit' },
    { id: 'lohnlauf', label: 'Lohnlauf' },
    { id: 'steuer_sozialversicherung', label: 'Steuer & Sozialversicherung' },
    { id: 'rechnungsdaten', label: 'Rechnungsdaten' },
    { id: 'datev', label: 'DATEV' },
    { id: 'fristen', label: 'Individuelle Fristen' },
    { id: 'anhaenge', label: 'Individuelle Anhänge' },
    { id: 'todos', label: 'Individuelle To-Dos' },
    { id: 'tickets', label: 'Tickets' }
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '1400px',
        height: '85vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ 
              margin: 0, 
              fontSize: '20px', 
              fontWeight: '600',
              color: '#111827' 
            }}>
              Kunde bearbeiten
            </h2>
            <p style={{ 
              margin: '4px 0 0 0', 
              fontSize: '14px', 
              color: '#6B7280' 
            }}>
              {customer.company}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
              style={{
                backgroundColor: '#FEE2E2',
                border: 'none',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => !isDeleting && (e.currentTarget.style.backgroundColor = '#FCA5A5')}
              onMouseLeave={(e) => !isDeleting && (e.currentTarget.style.backgroundColor = '#FEE2E2')}
              title="Kunde löschen"
            >
              <Trash2 size={18} color="#DC2626" />
            </button>
            <button
              onClick={handleClose}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={20} color="#6B7280" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '16px',
          padding: '0 24px',
          borderBottom: '1px solid #E5E7EB',
          overflowX: 'auto',
          flexShrink: 0
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '12px 0',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #3B82F6' : '2px solid transparent',
                color: activeTab === tab.id ? '#3B82F6' : '#6B7280',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {activeTab === 'datev' ? (
            <div>
              <div style={{
                backgroundColor: '#EFF6FF',
                border: '1px solid #BFDBFE',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#1E40AF'
              }}>
                <strong>Info:</strong> Verbinden Sie hier die DATEV-Schnittstellen dieses Kunden.
                Der DATEV-Login öffnet sich in einem separaten Fenster; nach dem Anmelden schließt
                es sich automatisch und der Status hier aktualisiert sich.
              </div>
              {((formData.beraternummer || '') !== (customer.beraternummer || '') || (formData.mandantennummer || '') !== (customer.mandantennummer || '')) ? (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#92400E',
                  lineHeight: '1.5'
                }}>
                  Es gibt ungespeicherte Änderungen an Berater- oder Mandantennummer. Bitte speichern
                  Sie zuerst über den Stammdaten-Reiter, damit die DATEV-Verbindung an die richtige
                  Berater-Mandanten-Kombination gebunden wird.
                </div>
              ) : (!customer.beraternummer || !customer.mandantennummer) ? (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#92400E',
                  lineHeight: '1.5'
                }}>
                  Für DATEV-Verbindungen müssen Berater- und Mandantennummer in den Stammdaten
                  hinterlegt und gespeichert sein.
                </div>
              ) : (
                <CustomerDatevPanel
                  customerId={customer.id}
                  beraternummer={customer.beraternummer}
                  mandantennummer={customer.mandantennummer}
                  onUpdate={() => setNeedsRefresh(true)}
                />
              )}
            </div>
          ) : activeTab === 'ansprechpartner' ? (
            <div>
              <div style={{
                backgroundColor: '#F0FDF4',
                border: '1px solid #BBF7D0',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#166534'
              }}>
                <strong>Info:</strong> Verwalten Sie hier die Ansprechpartner dieses Kunden. 
                Ansprechpartner mit der Option „Erhält eigenes Datenpaket" erscheinen als separate Finalisierungsschritte im Monatsabschluss.
              </div>
              <ContactsManager 
                customerId={customer.id}
                onUpdate={handleContactsUpdate}
              />
            </div>
          ) : activeTab === 'versand_sichtbarkeit' ? (
            <div>
              <div style={{
                backgroundColor: '#FFFBEB',
                border: '1px solid #FDE68A',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#92400E'
              }}>
                <strong>Info:</strong> Hier ist an einer Stelle zusammengefasst, wer welche Pakete erhält
                und wer Lohnscheine interner Mitarbeiter sehen darf. Änderungen wirken sofort, es gibt
                kein separates Speichern.
              </div>
              <BenachrichtigungszentrumSchalter
                customerId={customer.id}
                onUpdate={() => setNeedsRefresh(true)}
              />
              <VersandSichtbarkeitPanel
                customerId={customer.id}
                beraternummer={customer.beraternummer ?? null}
                mandantennummer={customer.mandantennummer ?? null}
                onUpdate={handleContactsUpdate}
              />
            </div>
          ) : activeTab === 'fristen' ? (
            <div>
              <div style={{
                backgroundColor: '#F0F9FF',
                border: '1px solid #BAE6FD',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#075985'
              }}>
                <strong>Info:</strong> Hier können Sie individuelle Fristen für diesen Kunden festlegen. 
                Diese erscheinen zusätzlich zu den Standard-Fristen in der Kundenkarte.
                <br /><br />
                <strong>Wiederkehrende Fristen:</strong> Erscheinen jeden Monat am selben Tag.
                <br />
                <strong>Einmalige Fristen:</strong> Erscheinen nur im spezifizierten Monat.
              </div>
              <IndividualDeadlinesManager 
                customerId={customer.id}
                onUpdate={handleDeadlinesUpdate}
              />
            </div>
          ) : activeTab === 'anhaenge' ? (
            <div>
              <div style={{
                backgroundColor: '#FEF3C7',
                border: '1px solid #FDE68A',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#92400E'
              }}>
                <strong>Info:</strong> Definieren Sie hier individuelle Anhänge, die für diesen Kunden bei jeder Finalisierung erforderlich sind. 
                Diese werden zusätzlich zur monatlichen Abrechnung angefordert.
              </div>
              <CustomAttachmentsManager 
                customerId={customer.id}
                onUpdate={handleAttachmentsUpdate}
              />
            </div>
          ) : activeTab === 'tickets' ? (
            <CustomerTicketsPanel customerId={customer.id} />
          ) : activeTab === 'todos' ? (
            <div>
              <div style={{
                backgroundColor: '#EDE9FE',
                border: '1px solid #DDD6FE',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#5B21B6'
              }}>
                <strong>Info:</strong> Erstellen Sie hier individuelle To-Dos, die bei jeder Finalisierung für diesen Kunden angezeigt werden. 
                Diese dienen als Erinnerung für wichtige Aufgaben.
              </div>
              <CustomTodosManager 
                customerId={customer.id}
                onUpdate={handleTodosUpdate}
              />
            </div>
          ) : (
            <CustomerForm
              formData={formData}
              activeTab={activeTab as 'stammdaten' | 'lohnlauf' | 'steuer_sozialversicherung' | 'rechnungsdaten'}
              errors={errors}
              onChange={handleFieldChange}
              showContactFields={false}
              lohnscheinConfigs={lohnscheinConfigs}
              onLohnscheinConfigChange={handleLohnscheinConfigChange}
              customerId={customer.id}
              pricingValue={pricingValue}
              onPricingChange={(v: number | null) => { setPricingValue(v); setIsDirty(true); }}
              stundensatzValue={stundensatzValue}
              onStundensatzChange={(v: number | null) => { setStundensatzValue(v); setIsDirty(true); }}
            />
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '12px'
        }}>
          {savedNotice && (
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginRight: 'auto',
              color: '#059669',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              <Check size={16} />
              Gespeichert
            </span>
          )}
          {saveError && (
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginRight: 'auto',
              color: '#DC2626',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {saveError}
            </span>
          )}
          {(activeTab !== 'fristen' && activeTab !== 'anhaenge' && activeTab !== 'todos' && activeTab !== 'ansprechpartner' && activeTab !== 'versand_sichtbarkeit' && activeTab !== 'datev' && activeTab !== 'tickets') && isDirty ? (
            <>
              <button
                onClick={handleDiscard}
                disabled={isUpdating}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isUpdating ? 0.6 : 1
                }}
              >
                Verwerfen
              </button>
              <button
                onClick={handleSave}
                disabled={isUpdating}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isUpdating ? '#9CA3AF' : '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {isUpdating ? 'Speichern...' : 'Speichern'}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              disabled={isUpdating}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isUpdating ? 0.6 : 1
              }}
            >
              Schließen
            </button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#111827'
            }}>
              Kunde löschen?
            </h3>
            <p style={{
              margin: '0 0 20px 0',
              fontSize: '14px',
              color: '#6B7280',
              lineHeight: '1.5'
            }}>
              Möchten Sie den Kunden <strong>{customer.company}</strong> wirklich löschen? 
              Der Kunde wird als gelöscht markiert und erscheint nicht mehr in den Übersichten.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isDeleting ? 0.6 : 1
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteCustomer}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isDeleting ? '#9CA3AF' : '#DC2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {isDeleting ? 'Lösche...' : 'Ja, löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
